
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { IKWFHubClient, IKWFServerState, IKWFClientState, IKWFHub } from './rwfsignalrtypes';
import { CompatibilityService } from './services/compatibilityservice';
import { IWebApiRoot } from './navdata';

/** Base 'angular(js)' unaware SignalR Communication object */
export class RwfSignalRService {

    /** Callbacks Observable */
    public readonly Callbacks = new BehaviorSubject<{ Event: keyof IKWFHubClient, Arguments: any[] }>(undefined);

    /** Current Application State */
    public readonly ApplicationState = new BehaviorSubject<IKWFServerState & IKWFClientState>(<any><Partial<IKWFClientState>>{

    });
    private _SignalR: SignalR;
    private _LoadScript: PromiseLike<any>;

    public readonly ConnectionFailure = new BehaviorSubject<boolean>(false);

    constructor(
        private readonly $c: CompatibilityService,
        /** Url to rwf */
        public readonly rwfUrl: string | IWebApiRoot | PromiseLike<string | IWebApiRoot> = undefined,
    ) {
    }

    /** Register for Callback notification for given eventName */
    public Register<TKey extends keyof IKWFHubClient>(eventName: TKey, callback: IKWFHubClient[TKey]) {
        const r = this.Callbacks.pipe(filter(x => x && eventName === x.Event)).subscribe(s => (<Function>callback).apply(undefined, s.Arguments));
        //Implicitly Ensure Connection (Really here(?))
        this.getKwfHub();
        return r;
    }

    /** Get (imp Create) KWF Hub, (so far destruction only on WindowUnload) */
    public getKwfHub(): PromiseLike<IKWFHub> {
        const ConnectionFailure = this.ConnectionFailure;
        const $c = this.$c;
        let result = this._getKWfHub;
        if (result) {
            // After Successful Connection : force reconnection if disconnected
            const reHub = this._SignalR && this._SignalR.hub;
            if (reHub) {
                return result.then(r => {
                    if (reHub.state === this._SignalR.connectionState.disconnected) {
                        console.log("SignalR: Detected disconnected state, attempting reconnection...");
                        ConnectionFailure.next(true);
                        // Reset the promise to force recreation
                        this._getKWfHub = null;
                        // Recursively call to create a new connection
                        return this.getKwfHub();
                    } else {
                        return result;
                    }
                });
            }
            return result;
        }

        if (!result) {
            return result = this._getKWfHub = this.getSignalR().then(signalR => {
                const kwfHub = <IKWFHub>signalR['kWFHub'];
                if (!kwfHub) {
                    return $c.reject<IKWFHub>('Hub "kwfHub" not found');
                }
                const clientInfo: Record<keyof IKWFHubClient, any> = {
                    onClientChange: 0,
                    onDeleteNotification: 0,
                    onEditChange: 0,
                    onEditedInstanceChanged: 0,
                    onForcedOpen: 0,
                    onPushNotification: 0,
                    onRequestClientState: 0,
                    onNavUiClosed: 0,
                    onRegisteredAsExternalSlave: 0,
                    onNotifyExternalSlaveRegistrationRequest: 0,
                    onNotifyExternalSlaveUnregistrationRequest: 0,
                    onNotifyExternalSlave: 0
                };

                Object.getOwnPropertyNames(clientInfo).forEach((clientName: keyof IKWFHubClient) => {
                    kwfHub.client[clientName] = (...args: any[]) => {
                        const _result = this.Callbacks.next({ Event: clientName, Arguments: args });
                        this.$c.tick();
                        return _result;
                    };
                });

                const hub = signalR.hub;

                return $c.when(hub.start({ jsonp: true })).then(() => {
                    hub.disconnected(() => {
                        console.log("SignalR Disconnected");
                        ConnectionFailure.next(true);
                        // Reset the hub promise to force recreation on next getKwfHub() call
                        this._getKWfHub = null;
                        // Start active reconnection attempts
                        this.scheduleReconnection(0);
                    });
                    hub.reconnected(() => {
                        console.log("SignalR Reconnected");
                        ConnectionFailure.next(false);
                        this.reloadApplicationState(kwfHub);
                    });
                    return this.reloadApplicationState(kwfHub).then(() => kwfHub);
                }, err => {
                    ConnectionFailure.next(true);
                    throw err;
                });
            });
        }
    }
    // tslint:disable-next-line:member-ordering
    private _getKWfHub: PromiseLike<IKWFHub>;
    private _reconnectionTimeoutId: ReturnType<typeof setTimeout> = null;

    private scheduleReconnection(retryCount: number): void {
        // Clear any existing reconnection timeout
        if (this._reconnectionTimeoutId) {
            clearTimeout(this._reconnectionTimeoutId);
        }

        const maxRetries = 5; // Reduced from 10 to limit NAV pressure
        const baseDelay = 2000; // Increased from 1000ms to reduce frequency
        const maxDelay = 60000; // Increased to 60s max for less aggressive reconnection

        if (retryCount >= maxRetries) {
            console.error("SignalR: Maximum reconnection attempts reached. Please refresh the page.");
            return;
        }

        // Less aggressive backoff: 2s, 4s, 8s, 16s, 32s, 60s
        const delay = Math.min(baseDelay * Math.pow(2, retryCount), maxDelay);

        // Add jitter to prevent thundering herd problem with multiple clients
        const jitter = Math.random() * 1000; // 0-1000ms random delay
        const finalDelay = delay + jitter;

        console.log(`SignalR: Attempting reconnection ${retryCount + 1}/${maxRetries} in ${Math.round(finalDelay/1000)}s...`);

        this._reconnectionTimeoutId = setTimeout(() => {
            console.log(`SignalR: Reconnection attempt ${retryCount + 1}/${maxRetries}`);

            // Attempt to get the hub (which will create a new connection)
            this.$c.when(this.getKwfHub()).then((kwfHub) => {
                console.log("SignalR: Successfully reconnected!");
                this.ConnectionFailure.next(false);
                // Reload application state to restore subscriptions and auto-refresh
                return this.reloadApplicationState(kwfHub);
            }).then(() => {
                // Clear the timeout ID since we succeeded
                this._reconnectionTimeoutId = null;
            }, (error) => {
                console.warn(`SignalR: Reconnection attempt ${retryCount + 1} failed:`, error);
                // Schedule next attempt
                this.scheduleReconnection(retryCount + 1);
            });
        }, finalDelay);
    }

    public releaseKWFHub() {
        // Clear any pending reconnection attempts
        if (this._reconnectionTimeoutId) {
            clearTimeout(this._reconnectionTimeoutId);
            this._reconnectionTimeoutId = null;
        }

        const connection = $.connection;
        if (connection && connection.hub) {
            connection.hub.stop(false, true);
        }
    }

    private getSignalR(): PromiseLike<SignalR> {
        if (this._SignalR) {
            return this.$c.when(this._SignalR);
        }

        if (!this._LoadScript) {
            this._LoadScript = this.getRwfUrl().then(rwfUrl => {
                const prefix = rwfUrl.replace(/\/+$/, '') || '.';
                return this.$c.when($.getScript(prefix + '/signalr/hubs'));
            });
        }

        return this._LoadScript.then(() => {
            if (this._SignalR) {
                return this._SignalR;
            }

            const signalr = $.connection;
            if (!signalr) {
                return this.$c.reject<SignalR>('SignalR not available');
            }
            return this.getRwfUrl().then(rwfUrl => {
                const prefix = rwfUrl.replace(/\/+$/, '') || '.';
                signalr.hub.url = prefix + '/signalr';
                return signalr;
            });
        });
    }

    private reloadApplicationState(kwfHub: IKWFHub): PromiseLike<any> {

        const thisM: this & { __Reload?: PromiseLike<any> } = <any>this;
        const clientState = this.getClientStateForServer();
        return thisM.__Reload || (thisM.__Reload = this.$c.when(
            kwfHub.server.getApplicationState(clientState)).then(state => {
                const value = this.ApplicationState.getValue();
                Object.getOwnPropertyNames(state).forEach(v => {
                    value[v] = state[v];
                });
                if (!value.SessionId) {
                    const err = 'Ungültige Information vom Server: Keine Information zur aktuellen SessionId';
                    this.ApplicationState.error(err);
                    throw new Error(err);
                }

                this.ApplicationState.next(value);
                return kwfHub;
            }).then(() => {
                delete thisM.__Reload;
                return kwfHub;
            }, (err) => { delete thisM.__Reload; throw err; }));
    }

    private getClientStateForServer(): IKWFClientState {
        const state: any = this.ApplicationState && this.ApplicationState.getValue && this.ApplicationState.getValue();
        if (!state) {
            return undefined;
        }

        // IMPORTANT: Only send the client state subset.
        // Sending the merged server state back to the server can blow up the JSONP/GET URL
        // (e.g. when ServerVersion contains ErrorDetails/stack traces), causing 400 responses
        // and breaking reconnection.
        const clientState: IKWFClientState = {
            StartUpTime: state.StartUpTime,
            LastRouteChange: state.LastRouteChange,
            CurrentUrl: state.CurrentUrl,
            Version: state.Version,
            UserAgent: state.UserAgent
        };

        // If nothing useful is present yet, don't send anything.
        if (!clientState.StartUpTime && !clientState.LastRouteChange && !clientState.CurrentUrl && !clientState.Version && !clientState.UserAgent) {
            return undefined;
        }

        return clientState;
    }

    private getRwfUrl() {
        return this.$c.when(this.rwfUrl).then(rwfUrl => {
            if (!(typeof rwfUrl === "string")) {
                rwfUrl = (rwfUrl && rwfUrl.WebApiRoot) || "";
            }
            return <string>rwfUrl || "";
        });
    }
}
